import { Link } from "react-router-dom";
import { Printer, Download, ArrowLeft, Quote, Target, Wrench, TrendingUp, CheckCircle2 } from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

const RESULTS = [
  { v: "$418K", l: "Net savings vs prior mat spend" },
  { v: "0", l: "Mat replacements over the project" },
  { v: "9 days", l: "Mobilization time recovered" },
  { v: "3.4 : 1", l: "Benefit-to-cost ratio" },
];

export default function CaseStudy() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/products/big-bad-mats" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm"><ArrowLeft className="h-4 w-4" /> Back to product</Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Case Study · One-Page</span>
            <a href="/big-bad-mats-case-study.pdf" download data-testid="download-case-study" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors"><Download className="h-4 w-4" /> Download PDF</a>
            <button onClick={() => window.print()} data-testid="print-case-study" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors"><Printer className="h-4 w-4" /> Print</button>
          </div>
        </div>
      </div>

      <div className="py-8 print:py-0 flex justify-center">
        <div data-testid="case-study-sheet" className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col" style={{ fontSize: "10px" }}>
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[66%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">Project Profile · Case Study</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[30px]">60-Day Pipeline ROW: 5,000 Mats, Zero Replacements.</h1>
              <p className="mt-2 text-charcoal text-[11px]">Heavy Civil / Pipeline Construction · Gulf Coast region · Saturated, low-CBR subgrade</p>
            </div>
            <img src={IMAGES.bigbadmats} alt="Big Bad Mats deployment" className="w-44 h-28 object-cover border border-border" />
          </header>

          <div className="flex-1 grid grid-cols-3 gap-4 pt-4 min-h-0">
            {[
              { icon: Target, title: "The Challenge", body: ["Right-of-way across **saturated, low-CBR soil.**", "Prior **composite mats cracked** under tracked loads.", "Recurring **re-buys and disposal fees.**", "**Weather delays** on unstable ground."] },
              { icon: Wrench, title: "The Deployment", body: ["**5,000** Big Bad Mats over 60 days.", "**Machine-set** via telehandler — no crane.", "**Fast-connect** locking, 2-person crews.", "Relocated across **multiple spreads.**"] },
              { icon: TrendingUp, title: "The Outcome", body: ["**Zero** mat failures or replacements.", "**Non-porous** surface — clean ROW handback.", "**Faster turns** between spreads.", "Reused on the **next project** intact."] },
            ].map((c) => (
              <div key={c.title} className="border border-border flex flex-col">
                <div className="bg-ink text-white px-4 py-2 flex items-center gap-2"><c.icon className="h-4 w-4 text-flame" /><span className="font-display font-bold text-[12px] uppercase tracking-wider">{c.title}</span></div>
                <ul className="p-4 space-y-2.5 flex-1 flex flex-col justify-center">
                  {c.body.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-charcoal text-[11px] leading-snug">
                      <CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-0.5" />
                      <span>{b.split(/(\*\*[^*]+\*\*)/g).map((s, i) => s.startsWith("**") ? <strong key={i} className="text-ink font-semibold">{s.slice(2, -2)}</strong> : s)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-4 bg-ink text-white px-6 py-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/50">Measured Results</div>
            <div className="mt-2 grid grid-cols-4 gap-3">
              {RESULTS.map((o) => (
                <div key={o.l} className="bg-white/5 border border-white/10 px-4 py-3">
                  <div className="font-display font-extrabold text-flame text-[24px] leading-none">{o.v}</div>
                  <div className="text-white/60 text-[9.5px] mt-1">{o.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-4 border-t border-border pt-3">
            <div className="flex items-start gap-2 max-w-[62%]">
              <Quote className="h-5 w-5 text-flame flex-shrink-0" />
              <p className="italic text-charcoal text-[11px] leading-snug">"We stopped re-ordering mats mid-project. They held up, cleaned up, and moved to the next spread with us."<span className="not-italic text-muted-foreground"> — Project Superintendent (representative)</span></p>
            </div>
            <Link to="/request-quote" className="inline-flex items-center bg-flame text-white px-5 py-3 font-semibold text-[12px] hover:bg-flame/90 transition-colors print:hidden flex-shrink-0">Request a Project Analysis</Link>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">Representative project profile for evaluation; figures are illustrative estimates. Not a quote, guarantee, or warranty of any specific result; not financial, engineering, or legal advice. Actual outcomes vary by site conditions &amp; usage. © {new Date().getFullYear()} {COMPANY.legal}.</p>
        </div>
      </div>
    </div>
  );
}
